import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function NewsTicker() {
  const [newsItems, setNewsItems] = useState([]);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        // Fetch manual news items
        const manualResponse = await fetch(`${API_URL}/api/news`);
        let manualNews = [];
        if (manualResponse.ok) {
          manualNews = await manualResponse.json();
        }

        // Fetch RSS news items
        const rssResponse = await fetch(`${API_URL}/api/news/rss`);
        let rssNews = [];
        if (rssResponse.ok) {
          rssNews = await rssResponse.json();
        }

        // Combine and sort by priority (manual items have priority field, RSS items don't)
        const allNews = [
          ...manualNews.map(n => ({ ...n, priority: n.priority || 0 })),
          ...rssNews.map(n => ({ ...n, priority: -1 })) // RSS items show after manual items
        ].filter(n => n.is_active !== false);

        // Sort by priority descending
        allNews.sort((a, b) => b.priority - a.priority);

        setNewsItems(allNews);
        setIsVisible(allNews.length > 0);
      } catch (err) {
        console.log('Failed to fetch news');
      }
    };

    fetchNews();
    // Refresh every 60 seconds
    const interval = setInterval(fetchNews, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!isVisible || newsItems.length === 0) return null;

  // Combine all news into one scrolling text
  const tickerText = newsItems.map(item => item.text).join('  •  ');

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-40 overflow-hidden"
      style={{ 
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        borderTop: '1px solid var(--surface-highlight)'
      }}
    >
      <div className="flex items-center h-8">
        {/* Label */}
        <div 
          className="flex-shrink-0 px-3 h-full flex items-center font-bold text-xs uppercase tracking-wider"
          style={{ 
            backgroundColor: 'var(--primary)',
            color: 'white'
          }}
        >
          News
        </div>
        
        {/* Scrolling content */}
        <div className="flex-1 overflow-hidden">
          <div 
            className="ticker-scroll whitespace-nowrap flex items-center h-8 text-sm"
            style={{ color: 'var(--text-main)' }}
          >
            {newsItems.map((item, index) => (
              <span key={item.news_id} className="inline-flex items-center">
                {item.link_url ? (
                  <a 
                    href={item.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline inline-flex items-center gap-1"
                    style={{ color: 'var(--primary)' }}
                  >
                    {item.text}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span>{item.text}</span>
                )}
                {index < newsItems.length - 1 && (
                  <span className="mx-4" style={{ color: 'var(--primary)' }}>•</span>
                )}
              </span>
            ))}
            {/* Duplicate for seamless loop */}
            <span className="mx-8" style={{ color: 'var(--primary)' }}>•</span>
            {newsItems.map((item, index) => (
              <span key={`dup-${item.news_id}`} className="inline-flex items-center">
                {item.link_url ? (
                  <a 
                    href={item.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline inline-flex items-center gap-1"
                    style={{ color: 'var(--primary)' }}
                  >
                    {item.text}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span>{item.text}</span>
                )}
                {index < newsItems.length - 1 && (
                  <span className="mx-4" style={{ color: 'var(--primary)' }}>•</span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={() => setIsVisible(false)}
          className="flex-shrink-0 px-3 h-full flex items-center text-xs hover:bg-white/10 transition-colors"
          style={{ color: 'var(--text-muted)' }}
        >
          ✕
        </button>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-scroll {
          animation: ticker 30s linear infinite;
        }
        .ticker-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}
